/**
 * `advisor-client`: the firm-side client record (crossmarc/steward-app#1763,
 * docs/35-advisor-client-onboarding.md "The client record"). One row per
 * prospect/client in a firm's own workspace, keyed by the person's normalised
 * email address rather than by anything the family controls. It is the join
 * point the new profile page reads by: a Prospects or Clients row now links to
 * `/advisor/clients/<clientId>`, where `clientId` is this row's own appcore
 * `instanceId` (never a `draftId`, an invitation id, or a submission id).
 *
 * ## Placement: `STEWARD_ADVISOR_FIRM_TYPE_DEFS`, not `steward-app`
 *
 * This is a coordinated placement with the appcore-side identity work
 * (crossmarc/appcore#736), fixed at design time rather than derived here from
 * the placement precedent `intake-invitation.ts` and `draftDismissal.ts` set
 * (both firm-scoped, both written on the user plane, both declared in
 * `STEWARD_APP_TYPE_DEFS`). That precedent's own reasoning — read at
 * `packages/steward-types/src/index.ts`'s `intake-invitation` entry, 2026-09-24
 * — is about a SYSTEM-PLANE bound (`requiredPack: 'steward-advisor-firm'`)
 * that only matters for a type a background Lambda actor reads or writes over
 * `/system/*`. That bound DOES apply here, since the intake writer creates these
 * rows over `/system/*` (below), but the placement was fixed first by the
 * appcore#736 contract. Capability resolution is unaffected by the placement,
 * because appcore resolves a caller's capabilities as
 * the flat union of every role on their membership, over every pack the
 * workspace mounts, never scoped to the pack that declares the type being
 * written (`index.ts`'s `intake-invitation` entry cites
 * `packages/core/src/auth/permissions.ts`'s `resolveRolePermissions`, read at
 * source in the appcore checkout, 2026-09-19) — so a firm workspace's
 * `steward-admin`/`steward-member` roles (carried on the `steward-app` pack
 * side of the SAME membership; a firm workspace mounts BOTH packs, confirmed
 * via `services/steward-app-api/src/__tests__/me.test.ts:289`) grant
 * `steward:write` on a type declared here. That is why this type does NOT
 * declare `steward:write` (crossmarc/steward-app#1781): it declares
 * `ADVISOR_CLIENT_WRITE_CAPABILITY`, which no pack role holds, so the
 * generic member-plane routes refuse every write while the pack definition is
 * the one resolved. A firm `steward-admin` can still shadow the type with a
 * workspace-local definition (`type:write`) and write again: that residual is
 * crossmarc/appcore#313. `advisorClient.test.ts` pins the declaration and
 * that no pack role holds the string, and nothing more.
 *
 * ## One row per email: the address slot holds it, the id is minted (2026-10-07)
 *
 * Rows are created by ONE process, the intake writer, over `/system/*` via
 * `@steward-app/appcore-system` (`services/intake-writer/src/clientRecord.ts`).
 * The row's `instanceId` is a ULID the writer mints. "One record per address
 * per firm" is held by the ADDRESS SLOT, a row in the claims table keyed by
 * (firm, exact-address hash) and written with `attribute_not_exists(pk)`
 * (`@steward-app/intake-claims`, `clientSlot.ts`): the loser of a race adopts
 * the winner's id. Until 2026-10-07 the id was a KMS MAC of the address, so the
 * same address always named the same id; a client deleted and invited again is
 * now a wholly new record with a new id and nothing carried over. Pinned by
 * `clientRecord.test.ts` ("two concurrent first ensures ...").
 *
  * What asks for a row: the façade enqueues a {@link ClientRecordEnsureMessage}
 * after `createIntakeInvitation` stores the invitation, and the writer ensures
 * one inline on a draft's first save, on a draft's submit, and on a public
 * submission ONLY when its address is verified
 * (`emailVerification.clientEmailMatches`). Every GET stays read-only
 * (`clientIdsByEmailKey`).
 *
 * ## How an intake joins a record: the stamped `clientId`, never the address
 *
 * When the writer creates an `advisor-intake` it stamps `clientId`, the id of
 * the record it just ensured, from an address a person proved or an advisor
 * typed. The façade joins a submission to a record by that stored value and
 * never by `clientEmail`, which on an anonymous submission is whatever was
 * typed into About You (delta review 2026-09-24). A submission with no stamp
 * is its own Prospects row with no profile: a public submission with no proven
 * address, or a draft submit whose ensure threw on every SQS receive (the
 * writer redelivers before that; only a later submit of the same draft repairs
 * the stamp). It is not necessarily marked unverified; see docs/35.
 *
 * ## No assigned-advisor field
 *
 * Deliberately absent, per crossmarc/steward-app#1763: assignment lives in
 * appcore custody (appcore#736), wired in a follow-up. Do not add it here even
 * as a display-only field.
 */
import { z } from 'zod';
import { ADVISOR_FIRM_SLUG_PATTERN } from './advisorFirmDirectory.js';
import { ULID_PATTERN } from './intakeMessage.js';

export const ADVISOR_CLIENT_TYPE_CODE = 'advisor-client';

/**
 * The cap on an address this row's `emailKey` (below) can hold, and so the cap every WRITE
 * PATH that can lead to a client record must itself refuse an address past, before a row is
 * even attempted (crossmarc/steward-app#1788). Shared rather than duplicated as a literal:
 * `onboardingInvitation.ts`'s and `intakeInvitation.ts`'s create-input schemas both import this
 * so an address their own `z.email()` accepts is always one {@link ClientRecordEnsureMessageSchema}
 * accepts too. Before #1788 those two create routes capped at 254 (appcore's own grant/claim
 * message caps), so an address of 201-254 characters passed create, minted a grant and sent
 * mail, but its `enqueueClientRecordEnsure` silently failed its own schema check and no client
 * record was ever written for it.
 */
export const ADVISOR_CLIENT_EMAIL_MAX = 200;

const id = z.string().min(1).max(200);
const instant = z.iso.datetime();

/** `.trim().toLowerCase()` — mirrors `AdvisorIntakes.tsx`'s `addressKey` (SPA-side; not
 *  imported here to avoid an app->types dependency) so the same address always resolves to
 *  the same record. Not a full RFC 5321 canonicalisation (no sub-addressing/case-folding of
 *  the local part beyond ASCII lowercase) — good enough for a dedup key, not for delivery. */
export function normalizeClientEmailKey(email: string): string {
  return email.trim().toLowerCase();
}

const stampValue = z.string().min(1).max(5000);

export const AdvisorClientAttributesSchema = z.strictObject({
  firmId: id,
  /** {@link normalizeClientEmailKey} applied to the address this record was first seen
   *  under. The dedup key. The façade returns it to the owning firm as the record's `email`
   *  (the profile header's primary address, #1763 review round 3), so it may be shown in
   *  lowercase even where the advisor typed capitals. */
  emailKey: id,
  createdAt: instant,
  createdBy: id,
  // Seven stamp fields (six set-once stamps plus the purge fence) appcore writes DIRECTLY onto the row (docs/43 §3.3, §3.5, §7; appcore
  // `db/client-record-stamps.ts`, crossmarc/steward-app#2699). They are deliberately NOT declared
  // on the `advisor-client` type def, so generic writes cannot set them; this READER must still
  // accept them, or a stamped row parses as `unreadable` and the client vanishes from the list,
  // the profile 404s and the purge refuses. Optional, and every other unknown key stays refused.
  // Deliberately TOLERANT (any non-empty string up to 5000 chars, not an ISO instant): appcore's
  // consent route validates `at` by shape only, and a malformed stamp must never make the client
  // vanish, which is the bug this fixes.
  // The façade's `ClientRecordRow` projection copies the four fields above plus THREE timestamps
  // (`sharingConsentAt`, `snapshotSentAt`, `onboardingSentAt`) as `stamps`, which the client-records
  // list returns for the Prospects stage. The other four fields are not copied. The façade test
  // `advisorClientRecord.test.ts` ("carries the record's set-once timestamps as `stamps`") pins the
  // three timestamps present and the consent text and the sender absent; it does not check the
  // consent version or `deletingAt`.
  sharingConsentAt: stampValue.optional(),
  sharingConsentText: stampValue.optional(),
  sharingConsentVersion: stampValue.optional(),
  snapshotSentAt: stampValue.optional(),
  snapshotSentBy: stampValue.optional(),
  onboardingSentAt: stampValue.optional(),
  /** appcore's `deleteAdvisorClient` writes this deletion fence first (appcore decisions log §105, `d1f621b`, read 2026-11-01); a stuck
   *  deletion must not make the client vanish from the firm's list. */
  deletingAt: stampValue.optional(),
  /**
   * The recorded verbal consent for staff entry (docs/43 §8.5, crossmarc/steward-app#2538), which
   * appcore writes onto this row (`client-record-stamps.ts`, `recordStaffEntryConsent`; docs/06 §34,
   * read 2026-11-02 at fdf4157 on `feat/2538-staff-answers`). Without it here the strict reader
   * would turn the row `unreadable` and the client would vanish from the firm's list at the first
   * recorded consent. A malformed one reads as absent (`.catch`), never as an unreadable row, for
   * the reason the stamps above give. Only its `at` leaves the façade ({@link staffEntryConsentAtOf}).
   */
  staffEntryConsent: z.object({
    at: stampValue,
    recordedBy: stampValue,
    method: z.literal('verbal'),
    textVersion: stampValue,
    text: stampValue,
  }).optional().catch(undefined),
  /**
   * The firm hid this in-progress prospect from Prospects (Matt, 2026-11-02): appcore's
   * `dismissProspect` (`client-record-stamps.ts`, `PUT /w/{ws}/advisor-clients/{clientId}/dismissal`,
   * read at appcore `feat/record-prospect-dismiss` on 2026-11-02) writes `{ at, by }` onto this row.
   * Tolerant for the same reason as the stamps above: a malformed one reads as absent (`.catch`),
   * never as an unreadable row, so a bad stamp can only fail OPEN (the prospect stays listed). Only
   * its `at` leaves the façade ({@link dismissedAtOf}).
   */
  dismissed: z.object({ at: stampValue, by: stampValue }).optional().catch(undefined),
});

export type AdvisorClientAttributes = z.infer<typeof AdvisorClientAttributesSchema>;

/** `dismissed.at` when the row carries a dismissal that reads as a stamp, else undefined. */
export function dismissedAtOf(c: Pick<AdvisorClientAttributes, 'dismissed'>): string | undefined {
  return c.dismissed?.at;
}

/** `staffEntryConsent.at` when the row carries one that reads as a stamp, else undefined. */
export function staffEntryConsentAtOf(c: Pick<AdvisorClientAttributes, 'staffEntryConsent'>): string | undefined {
  return c.staffEntryConsent?.at;
}

/** The charset appcore accepts for an instance id, and short enough to be one path segment of a
 *  signed `/system/*` URL. */
export const CLIENT_RECORD_ID_PATTERN = /^[A-Za-z0-9_.-]{1,64}$/;

/**
 * Is `id` a well-formed `advisor-client` id, for a READ route that puts it in a URL path?
 *
 * Exactly the one shape a row can have: the ULID the intake writer mints
 * ({@link ULID_PATTERN}, 26 Crockford base32 characters). Tighter than
 * {@link CLIENT_RECORD_ID_PATTERN} on purpose: that pattern admits `.` and so admits `.` and
 * `..`, which are path syntax, not ids. Takes `unknown` so a value read off a stored row can be
 * checked without a cast.
 *
 * Warning: a client id is now the same SHAPE as an intake or draft id. Nothing may tell them
 * apart by shape: a route that takes one reads its row by the type it asked for.
 */
export function isClientRecordId(id: unknown): id is string {
  return typeof id === 'string' && ULID_PATTERN.test(id);
}

/** The `kind` the intake writer dispatches an ensure on, ahead of the draft family's check. */
export const CLIENT_RECORD_ENSURE_KIND = 'ensure-client';

/**
 * `requestedBy` on a REPAIR ensure (crossmarc/steward-app#1765, design v2 F10): one the façade
 * sends on the client's own behalf when appcore answers `ONBOARDING_NOT_READY`, rather than on
 * an advisor's send. The intake writer's own actor string, which is also what it stamps as
 * `createdBy` on the rows it ensures unasked.
 *
 * 🚨 **The writer must never auto-assign on it.** Auto-assign adds the roster entry whose email
 * is `requestedBy`'s primary email, and a repair is triggered by the CLIENT's session: were it
 * to carry the client's userId, a client who is also on the firm's roster would become their
 * own assigned advisor, which is a full-SSN reveal basis. `clientRecord.test.ts` pins both
 * halves.
 */
export const CLIENT_RECORD_REPAIR_REQUESTED_BY = 'system:intake-writer';

/** An enqueue time in epoch ms. Bounded so an absurd value cannot read as a plausible send time. */
const QUEUE_SENT_AT_MS = z.number().int().min(1_000_000_000_000).max(10_000_000_000_000);

/**
 * "Make sure this firm has a client record for this address." Sent by the façade's API
 * Lambda on the IntakeQueue, consumed by the intake writer. Carries the firm SLUG, never a
 * workspace or tenant id: the writer resolves those itself from the published directory, so
 * a message cannot aim a write at a workspace. `requestedBy` is the advisor's userId, stamped
 * as the row's `createdBy`, or {@link CLIENT_RECORD_REPAIR_REQUESTED_BY} on a repair.
 */
export const ClientRecordEnsureMessageSchema = z.strictObject({
  kind: z.literal(CLIENT_RECORD_ENSURE_KIND),
  v: z.literal(1),
  firmId: z.string().regex(ADVISOR_FIRM_SLUG_PATTERN),
  // {@link ADVISOR_CLIENT_EMAIL_MAX}, not 254: the stored `emailKey` is capped there
  // (`AdvisorClientAttributesSchema`).
  email: z.string().min(3).max(ADVISOR_CLIENT_EMAIL_MAX),
  requestedBy: id,
  /**
   * Set by the invitation send only (2026-10-07): also mint a public sign-up grant for this
   * address at this firm, so the invited person can create a login. The writer mints it through
   * the SigV4 `/system/*` API; this flag carries no grant itself, and the address is the one the
   * signed-in advisor invited, never one a public caller supplied.
   */
  signupGrant: z.literal(true).optional(),
  /**
   * When the façade enqueued this message (its clock, epoch ms; crossmarc/steward-app#2180).
   * The writer compares it with the address slot's purge instant, which the façade's purge route
   * stamps from the SAME clock, to tell a message sent before a permanent delete (stale) from a
   * genuine re-invitation. Optional so a message already in flight when this shipped still
   * parses; the writer then falls back to SQS `SentTimestamp`, and a message with neither is
   * treated as stale. See `isFencedMessage` in the intake writer.
   */
  sentAtMs: QUEUE_SENT_AT_MS.optional(),
});

export type ClientRecordEnsureMessage = z.infer<typeof ClientRecordEnsureMessageSchema>;

/** The `kind` the intake writer dispatches an onboarding prefill seed on (#1881). */
export const ONBOARDING_PREFILL_SEED_KIND = 'onboarding-prefill-seed';

/**
 * "Seed this client's onboarding prefill from their submitted snapshot, if they have none"
 * (crossmarc/steward-app#1881). Sent by the façade when an advisor sends (or resends) an
 * onboarding invitation to a client who has a submitted `advisor-intake`; consumed by the
 * intake writer, the only writer of `onboarding-prefill` rows.
 *
 * Why it exists: the writer derives the prefill on each DRAFT save and at submit. A snapshot
 * submitted before that writer shipped (2026-09-25 on dev) never got a row, and nothing else
 * ever writes one, so its client's form opened empty. This is the lazy backfill, at the one
 * moment the prefill starts to matter.
 *
 * Carries the firm SLUG, like the ensure: the writer resolves the workspace itself. `intakeId`
 * is only a pointer the writer re-checks: it writes nothing unless that intake, read from the
 * resolved firm's own workspace, is stamped with the SAME client id the writer derives from
 * `email` (`processOnboardingPrefillSeedMessage`, `services/intake-writer/src/clientRecord.ts`).
 * Separate from the ensure message rather than a new field on it, because the ensure's schema
 * is strict: a writer one deploy behind would refuse an ensure carrying an unknown key, where
 * it merely acks and drops a kind it does not know.
 */
export const OnboardingPrefillSeedMessageSchema = z.strictObject({
  kind: z.literal(ONBOARDING_PREFILL_SEED_KIND),
  v: z.literal(1),
  firmId: z.string().regex(ADVISOR_FIRM_SLUG_PATTERN),
  email: z.string().min(3).max(ADVISOR_CLIENT_EMAIL_MAX),
  // A ULID: the writer mints every `advisor-intake` id (`draft.ts`), and the value becomes a
  // path segment on a signed request.
  intakeId: z.string().regex(ULID_PATTERN),
  /** The façade's enqueue time (epoch ms), as on {@link ClientRecordEnsureMessageSchema}. */
  sentAtMs: QUEUE_SENT_AT_MS.optional(),
});

export type OnboardingPrefillSeedMessage = z.infer<typeof OnboardingPrefillSeedMessageSchema>;

/** Reads a stored `advisor-client` row's attributes. `unreadable` covers both "does not
 *  parse" and "is missing", matching `readDraftDismissal`'s convention. */
export function readAdvisorClient(
  value: unknown,
): { kind: 'client'; client: AdvisorClientAttributes } | { kind: 'unreadable' } {
  const parsed = AdvisorClientAttributesSchema.safeParse(value);
  return parsed.success ? { kind: 'client', client: parsed.data } : { kind: 'unreadable' };
}
